import { EventType } from '@graphql/generated-base';
import type { GuestIdentity } from './guestIdentity.types';

export type { GuestIdentity } from './guestIdentity.types';

/**
 * The single place the guest-identity decision is made.
 *
 * It exists because three things have to agree — which picker opens, how a draft persists its
 * recipients, and which send mutation is called. They were each deriving it independently from
 * `eventType === EventType.general`, so changing the picker gate alone desynchronised them: a
 * new-stack wedding got the ContactPicker (contact id in `id`, guestKey in `personIdTag`) while the
 * draft and send paths still read `id` and labelled it a `personId`. That submits contact ids where
 * legacy person ids are expected, and nothing downstream rejects it. See MGT-69 / MGT-70.
 *
 * A new-stack wedding is still `eventType: wedding`, so event type cannot answer this on its own —
 * `useIsNewStackEvent` is the canonical gate.
 *
 * Deliberately not keyed on "has a Firebase document": this is the product question (which world
 * does this event's guest list live in), not the mechanical one.
 */
export const resolveGuestIdentity = (
  args: Readonly<{
    eventType: EventType | null | undefined;
    isNewStackEvent: boolean;
    isLoading: boolean;
  }>,
): GuestIdentity => {
  const { eventType, isNewStackEvent, isLoading } = args;

  // Keyed on the event-info query's own loading flag, not on a missing eventType: a surface with no
  // event at all settles to `loading: false` with no eventInfo, and that legitimately means the
  // legacy default rather than an answer still in flight.
  //
  // `isLoading` means "no event data yet", not "a request is in flight". EventInfo.provider exposes
  // `loading: !eventInfoData && loading`, so a background refetch under its `cache-and-network`
  // nextFetchPolicy leaves this false while the cached event stays readable. Do not weaken this to
  // `!eventType && isLoading`: it reads as equivalent under that invariant, but if the invariant
  // ever breaks it resolves a loading new-stack wedding to `personId` — the exact bug above — where
  // this fails safe instead.
  if (isLoading) return 'loading';

  return isNewStackEvent || eventType === EventType.general ? 'guestKey' : 'personId';
};
